import {
  createElement,
  forwardRef,
  type CSSProperties,
  type ElementType,
  type ReactNode,
} from 'react';
import { extractSystemProps } from 'src/components/md/system-props';

/**
 * Tailwind/style-based replacement for the former `styled(Typography)` root.
 * Reproduces the MD typography scale (variant → size/weight/line-height), color tokens,
 * and gradient text, and delegates MUI system props + `sx` to {@link extractSystemProps}.
 */

interface OwnerState {
  color: string;
  textTransform: string;
  verticalAlign: string;
  fontWeight?: 'light' | 'regular' | 'medium' | 'bold';
  opacity: number;
  textGradient: boolean;
  darkMode: boolean;
}

const COLORS: Record<string, string> = {
  inherit: 'inherit',
  white: '#ffffff',
  primary: '#1c1917',
  secondary: '#57534e',
  info: '#0ea5e9',
  success: '#84cc16',
  warning: '#eab308',
  error: '#ef4444',
  light: '#e7e5e4',
  dark: '#1c1917',
  text: '#292524',
};

const GRADIENTS: Record<string, string> = {
  primary: 'linear-gradient(195deg, #1c1917, #0c0a09)',
  secondary: 'linear-gradient(195deg, #57534e, #44403c)',
  info: 'linear-gradient(195deg, #0ea5e9, #0284c7)',
  success: 'linear-gradient(195deg, #84cc16, #65a30d)',
  warning: 'linear-gradient(195deg, #eab308, #ca8a04)',
  error: 'linear-gradient(195deg, #ef4444, #dc2626)',
  dark: 'linear-gradient(195deg, #1c1917, #0c0a09)',
};

const FONT_WEIGHTS: Record<string, number> = {
  light: 300,
  regular: 400,
  medium: 500,
  bold: 700,
};

// MD typography scale (matches src/assets/md-theme/base/typography.ts)
const VARIANTS: Record<string, CSSProperties> = {
  h1: { fontSize: '3rem', fontWeight: 700, lineHeight: 1.25 },
  h2: { fontSize: '2.25rem', fontWeight: 700, lineHeight: 1.3 },
  h3: { fontSize: '1.875rem', fontWeight: 700, lineHeight: 1.375 },
  h4: { fontSize: '1.5rem', fontWeight: 700, lineHeight: 1.375 },
  h5: { fontSize: '1.25rem', fontWeight: 700, lineHeight: 1.375 },
  h6: { fontSize: '1rem', fontWeight: 700, lineHeight: 1.625 },
  subtitle1: { fontSize: '1.25rem', fontWeight: 300, lineHeight: 1.625 },
  subtitle2: { fontSize: '1rem', fontWeight: 300, lineHeight: 1.6 },
  body1: { fontSize: '1.25rem', fontWeight: 400, lineHeight: 1.625 },
  body2: { fontSize: '1rem', fontWeight: 300, lineHeight: 1.6 },
  button: { fontSize: '0.875rem', fontWeight: 300, lineHeight: 1.5 },
  caption: { fontSize: '0.75rem', fontWeight: 300, lineHeight: 1.25 },
  overline: { fontSize: '0.75rem', fontWeight: 300, lineHeight: 1.25 },
};

const DEFAULT_TAG: Record<string, ElementType> = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
  subtitle1: 'p',
  subtitle2: 'p',
  body1: 'p',
  body2: 'p',
  button: 'span',
  caption: 'span',
  overline: 'span',
};

interface Props {
  ownerState: OwnerState;
  variant?: string;
  component?: ElementType;
  className?: string;
  children?: ReactNode;
  [key: string]: unknown;
}

const MDTypographyRoot = forwardRef<HTMLElement, Props>(
  function MDTypographyRoot(props, ref) {
    const { ownerState, variant, component, className, children, ...rest } =
      props as Props & { ownerState: OwnerState };
    const {
      color,
      textTransform,
      verticalAlign,
      fontWeight,
      opacity,
      textGradient,
    } = ownerState;

    const variantStyle: CSSProperties = variant
      ? (VARIANTS[variant] ?? {})
      : {};

    const base: CSSProperties = {
      margin: 0,
      ...variantStyle,
      textDecoration: 'none',
    };
    if (textTransform && textTransform !== 'none')
      base.textTransform = textTransform as CSSProperties['textTransform'];
    if (verticalAlign && verticalAlign !== 'unset')
      base.verticalAlign = verticalAlign as CSSProperties['verticalAlign'];
    if (opacity !== 1) base.opacity = opacity;
    if (fontWeight && FONT_WEIGHTS[fontWeight])
      base.fontWeight = FONT_WEIGHTS[fontWeight];

    if (textGradient) {
      base.backgroundImage = GRADIENTS[color] ?? GRADIENTS.dark;
      base.display = 'inline-block';
      (base as Record<string, unknown>).WebkitBackgroundClip = 'text';
      (base as Record<string, unknown>).WebkitTextFillColor = 'transparent';
    } else {
      base.color = COLORS[color] ?? COLORS.dark;
    }

    const { style, rest: domProps } = extractSystemProps(rest);
    const Component =
      (component as ElementType) ??
      (variant ? DEFAULT_TAG[variant] : undefined) ??
      'span';

    return createElement(
      Component,
      {
        ref,
        className: className as string | undefined,
        style: { ...base, ...style },
        ...domProps,
      },
      children as ReactNode,
    );
  },
);

export default MDTypographyRoot;
